import { Card, Loading, BaseTable, TableHeader, ActionButton, Button, Input, DateInput, Select, Textarea } from "components"
import moment from "moment"
import { FormStateProjectProfile } from "../types/ProfileTypes"
import { useApi, useEmployee } from "utils"
import { useChecklist } from "../utils/useChecklist";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChecklistValdationSchema, FormChecklistValdationSchema, validationChecklistSchema, validationFormChecklistSchema } from "../types/ChecklistSchema";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { PROJECT_STATUS_OPTIONS } from "../utils/projectTypeOption";

type TabChecklistProps = {
  action?:string
  project_id?:string
  approval_view?:boolean
  data_profile?:FormStateProjectProfile
}

export const TabChecklist = (props: TabChecklistProps) => {
  const api = useApi();
  const dataEmployee = useEmployee()
  const getChecklist = useChecklist(props.project_id!)

  const durationDays = getChecklist?.data?.start_date ? moment(getChecklist?.data?.due_date).diff(moment(moment().toDate()), "days") : 0
  const startWeeks = getChecklist?.data?.start_date ? Math.ceil(moment(moment().toDate()).diff(moment(getChecklist?.data?.start_date), "days") / 7 ) : 0;

  const [updateData, setUpdateData] = useState<number | null>(null)

  const defaultValues = { checklist_data: [] }
  const methods = useForm<FormChecklistValdationSchema>({
    defaultValues,
    resolver: yupResolver(validationFormChecklistSchema)
  })

  const { fields, update } = useFieldArray({
    control: methods.control,
    name: "checklist_data"
  })

  // Lookup boq_name & vendor_name langsung dari response BE
  const boqInfoMap = useMemo(() => {
    const map = new Map<number, { boq_name: string; vendor_name: string }>();
    (getChecklist?.data?.checklist_data ?? []).forEach((item: any) => {
      if (item.project_boq_id) {
        map.set(Number(item.project_boq_id), {
          boq_name: item.boq_name ?? "-",
          vendor_name: item.vendor_name ?? "-",
        });
      }
    });
    return map;
  }, [getChecklist?.data?.checklist_data]);

  const handleSaveClick = async () => {
    const currentValues = methods.getValues();

    const filledItems = currentValues?.checklist_data?.filter(item =>
      item.vendor_id > 0 &&
      item.checklist_status > 0 &&
      item.employee_id > 0 &&
      !!item.checklist_date &&
      !!item.detail &&
      !!item.description
    );

    if (!filledItems?.length) {
      toast.error("Minimal satu checklist harus diisi lengkap");
      return;
    }

    // Group by project_boq_id agar backend bisa update/cleanup per BOQ dengan benar
    const grouped = filledItems.reduce((acc, item) => {
      const key = item.project_boq_id;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {} as Record<number, ChecklistValdationSchema[]>);

    await toast.promise(
      Promise.all(Object.values(grouped).map(items => api.post("/project/savechecklist/", items))),
      {
        loading: "Loading...",
        success: "Berhasil disimpan",
        error: (err) => err?.response?.data?.detail?.message ?? err?.response?.data?.message
      }
    )
    .then(() => {
      getChecklist.refetch();
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    })
    .catch((err) => {
      throw new Error(err);
    });
  };
  useEffect(() => {
    if (getChecklist?.data) {
      // BE sudah kembalikan semua BOQ (termasuk yang belum ada checklist) via outerjoin
      const mapped = (getChecklist?.data?.checklist_data ?? []).map((item: any) => ({
        project_checklist_id: item.project_checklist_id ?? 0,
        project_boq_id: item.project_boq_id ?? 0,
        vendor_id: item.vendor_id ?? 0,
        checklist_status: item.checklist_status ?? 0,
        detail: item.detail ?? "",
        checklist_date: item.checklist_date ?? "",
        description: item.description ?? "",
        employee_id: item.employee_id ?? 0,
      }));
      methods?.setValue("checklist_data", mapped);
    }
    // eslint-disable-next-line
  }, [getChecklist?.data])

  if(getChecklist?.isLoading) {
    return (
      <Loading loading={getChecklist?.isLoading} errorText="No data available" />
    )
  }

  return(
    <div>
      <Card>
        <Card.Body>
          <div className="flex flex-row w-full"> 
            <div className="grow">
              <div className="text-2xl font-bold uppercase py-4">
                {getChecklist?.data?.project_name}
              </div>

              <table>
                <tbody>
                  <tr>
                    <td style={{ width: 100 }}>START DATE</td>
                    <td>:</td>
                    <td>{(getChecklist?.data?.start_date) ? moment(getChecklist?.data?.start_date)?.format('DD/MM/YYYY') : '-'}</td>
                  </tr>
                  <tr>
                    <td>DUE DATE</td>
                    <td>:</td>
                    <td>{(getChecklist?.data?.due_date) ? moment(getChecklist?.data?.due_date)?.format('DD/MM/YYYY') : '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex gap-2">
              <div className="min-h-[80px] flex flex-col border border-black">
                <div className="min-h-[40px] bg-black font-bold text-white text-sm text-center py-2 px-2">DURATION | DAYS</div>

                <div className="grow font-bold flex flex-col justify-center gap-1 p-3 text-center">
                  <span className="text-lg">{durationDays}</span>
                  <span className="text-sm">Leaf of days</span>
                </div>
              </div>
              <div className="min-h-[80px] flex flex-col border border-black">
                <div className="min-h-[40px] bg-black font-bold text-white text-sm text-center py-2 px-2">START | WEEKS</div>

                <div className="grow font-bold flex flex-col justify-center gap-1 p-3 text-center">
                  <span className="text-lg">{startWeeks}</span>
                  <span className="text-sm">Weeks</span>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <FormProvider {...methods}>
        <Card>
          <Card.Body>
            <div className="overflow-x-auto">
              <BaseTable className="border-collapse">
                <TableHeader>
                  <tr>
                    <th style={{ width: 50 }}>NO</th>
                    <th>BOQ</th>
                    <th>RESOURCE</th>
                    <th>STATUS</th>
                    <th>DETAIL</th>
                    <th>DESCRIPTION</th>
                    <th>CHECK DATE</th>
                    <th>CHECKER</th>
                    <th style={{ width: 50 }}>ACTION</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {!fields?.length! ? <tr><td colSpan={9} className="text-center">No data available</td></tr> : fields?.map((item, index) => (
                    <Controller
                      key={item?.id}
                      control={methods.control}
                      name={`checklist_data.${index}`}
                      render={({ field }) => (
                        <>
                          {updateData === index && (
                            <TableFormChecklist
                              project_id={Number(props?.project_id)}
                              key={item?.id}
                              index={updateData}
                              boq_name={boqInfoMap.get(field.value.project_boq_id)?.boq_name}
                              vendor_name={boqInfoMap.get(field.value.project_boq_id)?.vendor_name}
                              defaultValues={field.value}
                              onCancelEditing={() => setUpdateData(null)}
                              onSubmitData={(value) => {
                                update(index, value)
                                setUpdateData(null)
                              }}
                            />
                          )}
                          {updateData !== index && (
                            <tr key={index} >
                              <td className="text-center">{index + 1}</td>
                              <td>{boqInfoMap.get(item.project_boq_id)?.boq_name ?? "-"}</td>
                              <td>{boqInfoMap.get(item.project_boq_id)?.vendor_name ?? "-"}</td>
                              <td>{item?.checklist_status ? PROJECT_STATUS_OPTIONS?.find(status => Number(status?.value) === item?.checklist_status)?.label : "-"}</td>
                              <td><span className="whitespace-pre-wrap">{item?.detail ?? "-"}</span></td>
                              <td>{item?.description ?? "-"}</td>
                              <td>{item?.checklist_date ? moment(item?.checklist_date).format('DD/MM/YYYY') : "-"}</td>
                              <td>{item?.employee_id ? dataEmployee?.dataEmployee?.find(emp => Number(emp?.value) === item?.employee_id)?.label : "-"}</td>
                              <td>
                                <div className="flex justify-center items-center gap-1">
                                  {!Boolean(props.action === 'view') && (
                                    <ActionButton.Update
                                      permission="AT021"
                                      style={{ backgroundColor: "#f2f2f2", padding: 5, borderRadius: 5 }}
                                      onClick={() => setUpdateData(index)}
                                    />
                                  )}  
                                </div>
                              </td>
                            </tr> 
                          )}
                        </>
                      )}
                    /> 
                  ))}

                </tbody>
              </BaseTable>
            </div>

            <div className="flex justify-end mt-[30px] mb-[30px]">
              <Button color="primary" 
                disabled={Boolean(props.action === 'view')}
                className="min-w-[100px]"
                type="button"
                permission="ST19"
                onClick={handleSaveClick}
              >
                {props.action === 'edit' ? 'SAVE': 'CREATE'}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </FormProvider>
    </div>
  )
}

/** ----------------------- Form Section ----------------------- */
type TableFormChecklistProps = {
  index?: number
  defaultValues?: ChecklistValdationSchema
  boq_name?: string
  vendor_name?: string
  onCancelEditing?: () => void
  onSubmitData: (value: ChecklistValdationSchema) => void
  project_id: number
}

const TableFormChecklist = (props: TableFormChecklistProps) => {
  const dataEmployee = useEmployee()

  const defaultValues: ChecklistValdationSchema = useMemo(() => ({
    project_checklist_id: props?.defaultValues?.project_checklist_id ?? 0,
    project_boq_id: props?.defaultValues?.project_boq_id ?? 0,
    vendor_id: props?.defaultValues?.vendor_id ?? 0,
    checklist_status: props?.defaultValues?.checklist_status ?? 0,
    detail: props?.defaultValues?.detail ?? "",
    checklist_date: props?.defaultValues?.checklist_date ?? "",
    description: props?.defaultValues?.description ?? "",
    employee_id: props?.defaultValues?.employee_id ?? 0,
  }), [props.defaultValues]);

  const methods = useForm<ChecklistValdationSchema>({
    defaultValues,
    resolver: yupResolver(validationChecklistSchema)
  })

  useEffect(() => {
    methods.reset(defaultValues)
    // eslint-disable-next-line
  },[defaultValues])

  return (
    <FormProvider {...methods}>
      <tr className="no-padding-body">
        <td className="text-center">{props.index !== undefined ? props.index + 1 : ""}</td>
            <td className="font-medium px-4">{props.boq_name ?? "-"}</td>
            <td className="font-medium px-4">{props.vendor_name ?? "-"}</td>
            <td>
              <Controller
                control={methods.control}
                name="checklist_status"
                render={({ field, fieldState }) => (
                  <Select
                    withOutErrorMessage
                    className="w-full"
                    placeholder="Select status"
                    options={PROJECT_STATUS_OPTIONS}
                    value={field.value ? PROJECT_STATUS_OPTIONS?.find((item) => Number(item.value) === field.value) : null}
                    onChange={(e) => field.onChange(e?.value)}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </td>
            <td>
              <Controller
                control={methods.control}
                name="detail"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder={"1. ...\n2. ...\n3. ..."}
                    className="w-full"
                    rows={4}
                  />
                )}
              />
            </td>
            <td>
              <Controller
                control={methods.control}
                name="description"
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    withOutErrorMessage
                    placeholder="Input description"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </td>
            <td>
              <Controller
                control={methods.control}
                name="checklist_date"
                render={({ field, fieldState }) => (
                  <DateInput
                    withOutErrorMessage
                    portalId="root-portal"
                    popperPlacement="bottom-start"
                    placeholderText="Select check date"
                    selected={field.value ? moment(field.value).toDate() : null }
                    onChange={(value) => field.onChange(moment(value).format("YYYY-MM-DD"))}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </td>
            <td>
              <Controller
                control={methods.control}
                name="employee_id"
                render={({ field, fieldState }) => (
                  <Select
                    withOutErrorMessage
                    className="w-full"
                    placeholder="Select checker"
                    options={dataEmployee?.dataEmployee}
                    value={field.value ? dataEmployee?.dataEmployee?.find((item) => Number(item.value) === field.value) : null}
                    onChange={(e) => field.onChange(e?.value)}
                    error={fieldState.error?.message ?? ""}
                  />
                )}
              />
            </td>
            <td className="text-center">
              <div className="flex justify-center items-center gap-1 mt-[9px]">
                <ActionButton.Close
                  permission="AT021"
                  style={{ backgroundColor: "#f2f2f2", padding: 5, borderRadius: 5 }}
                  onClick={props?.onCancelEditing}
                />
                <ActionButton.Add
                  permission="AT021"
                  style={{ backgroundColor: "#f2f2f2", padding: 5, borderRadius: 5 }}
                  onClick={() => methods.handleSubmit((data) => props.onSubmitData(data))()}
                />
              </div>
            </td>
      </tr>
    </FormProvider>
  )
}